import React, { useState } from "react";
import { Search as SearchIcon, Loader2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { adminApi } from "../lib/api";
import StatusBadge from "../components/StatusBadge";

const TYPES = ["loi", "decret", "arrete", "circulaire", "ordonnance"];
const STATUSES = ["en_vigueur", "abroge", "modifie"];

export default function AdminSearch() {
  const { t, locale } = useI18n();
  const [query, setQuery] = useState("");
  const [docType, setDocType] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await adminApi.search({
        query,
        document_type: docType || undefined,
        status: status || undefined,
        language: locale,
        limit: 15,
      });
      setResults(data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-search-page">
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-2">
          / recherche
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-primary tracking-tight">
          {t("searchP.title")}
        </h1>
        <p className="text-sm text-foreground/60 mt-2">{t("searchP.subtitle")}</p>
      </div>

      <form
        onSubmit={submit}
        className="bg-white border border-primary/15 rounded-sm p-5 mb-6 space-y-3"
        data-testid="search-form"
      >
        <div className="flex items-center gap-2 border border-primary/20 rounded-sm px-3 focus-within:border-primary">
          <SearchIcon size={15} className="text-primary/60" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("searchP.placeholder")}
            className="flex-1 outline-none py-2.5 text-sm bg-transparent"
            data-testid="search-query-input"
          />
        </div>

        <div className="flex flex-wrap gap-3 items-end">
          <Select
            label={t("documents.filterType")}
            value={docType}
            onChange={setDocType}
            options={[
              { value: "", label: t("common.all") },
              ...TYPES.map((v) => ({ value: v, label: t(`types.${v}`) })),
            ]}
            testid="search-filter-type"
          />
          <Select
            label={t("documents.filterStatus")}
            value={status}
            onChange={setStatus}
            options={[
              { value: "", label: t("common.all") },
              ...STATUSES.map((v) => ({ value: v, label: t(`status.${v}`) })),
            ]}
            testid="search-filter-status"
          />
          <button
            type="submit"
            disabled={loading}
            className="ml-auto inline-flex items-center gap-1.5 bg-primary text-white px-4 py-2.5 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors disabled:opacity-60"
            data-testid="search-submit-btn"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <SearchIcon size={14} />}
            {t("searchP.submit")}
          </button>
        </div>
      </form>

      {error && (
        <div className="mb-4 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] px-3 py-2 rounded-sm">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[11px] uppercase tracking-widest text-foreground/50">
          {results.length} {t("searchP.results")}
        </div>
      </div>

      {results.length === 0 && !loading && (
        <div
          className="bg-white border border-primary/10 rounded-sm p-10 text-center text-foreground/50 text-sm"
          data-testid="search-empty"
        >
          {t("searchP.noResults")}
        </div>
      )}

      <ul className="space-y-3" data-testid="search-results">
        {results.map((r) => (
          <li
            key={r.chunk_id}
            className="bg-white border border-primary/15 rounded-sm p-5 hover:border-accent transition-colors"
            data-testid={`search-result-${r.chunk_id}`}
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-foreground/50">
                    {t(`types.${r.document_type}`, r.document_type)}
                  </span>
                  <StatusBadge
                    status={r.status}
                    label={t(`status.${r.status}`, r.status)}
                  />
                </div>
                <h3 className="font-display text-base font-semibold text-foreground leading-tight">
                  {r.document_title}
                </h3>
                <div className="font-mono text-xs text-primary mt-1">
                  {r.document_reference}
                  {r.article_reference ? ` · ${r.article_reference}` : ""}
                  {r.page_number ? ` · p.${r.page_number}` : ""}
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/50">
                  {t("searchP.similarity")}
                </div>
                <div className="font-mono text-sm text-accent-dark font-semibold">
                  {(r.similarity * 100).toFixed(0)}%
                </div>
              </div>
            </div>
            <p className="text-sm text-foreground/75 leading-relaxed italic mt-2 font-body">
              « {r.excerpt} »
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="flex flex-col">
      <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-foreground/50 mb-1">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="px-3 py-2 bg-white border border-primary/20 rounded-sm text-sm min-w-[150px] focus:border-primary outline-none cursor-pointer"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
